import React, { useState } from "react";

import {
  mdiAccountConvert,
  mdiArchive,
  mdiCog,
  mdiDelete,
  mdiEye,
  mdiLoading,
  mdiPencil,
  mdiTrashCanOutline,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { notifError } from "../../actions/FlashMessageActions";
import { delApi, getApi, postApi } from "../../actions/SendActions";
import AdminUserAPI from "../../api/AdminUserAPI";
import "../../css/admin/utils/settings-action.scss";
import { BUTTON_VARIANTS } from "../button/Button";
import history from "../common/navigation/AppHistory";
import ConfirmModal from "../modal/ConfirmModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import type { AppState } from "../../reducers";

interface SettingsActionItem {
  email?: string;
  label?: string;
  uuid: string;
  is_archivable?: boolean;
  is_deletable?: boolean;
  is_editable?: boolean;
}

interface StatusResponse {
  email?: string;
  label?: string;
  uuid: string;
  is_archivable?: boolean;
  is_deletable?: boolean;
  is_editable?: boolean;
}

type SettingsActionType = "archive" | "delete";

export interface SettingsActionProps {
  archive_body?: string;
  onArchive?: () => void;
  onOpenChange?: (open: boolean) => void;
  delete_body?: string;
  className?: string;
  editable?: boolean;
  edit_endpoint?: string;
  endpoint: string;
  hide_edit?: boolean;
  item: SettingsActionItem;
  show_or_see?: boolean;
  get_status?: boolean;
  onDelete?: () => void;
  type: string;
  url_history?: string;
  warning?: React.ReactNode;
}

function SettingsAction({
  archive_body,
  onArchive,
  onOpenChange,
  delete_body,
  className = "",
  editable = true,
  edit_endpoint,
  endpoint,
  hide_edit = false,
  item,
  show_or_see = false,
  get_status = true,
  onDelete,
  type,
  url_history,
  warning,
}: SettingsActionProps): React.JSX.Element {
  const dispatch = useDispatch();
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-content");
  const { token } = useSelector((state: AppState) => state.loginReducer);

  const impersonateUser = useMutation({
    mutationFn: async (userId: string) =>
      AdminUserAPI.impersonateUser({
        userId,
        projectSlug,
      }),
    onError: (error: unknown) => {
      console.error(error);
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    },
  });

  const [opened, setOpened] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [action, setAction] = useState<SettingsActionType | null>(null);
  const [current, setCurrent] = useState({
    email: item.email,
    label: item.label,
    uuid: item.uuid,
    is_archivable: item.is_archivable,
    is_deletable: item.is_deletable,
    is_editable: item.is_editable && editable,
    type: type.toLowerCase(),
  });

  const onConfirm = () => {
    switch (action) {
      case "archive":
        dispatch(
          postApi({
            endpoint: `/api/media/v6/admin/medias/${current.uuid}/archive`,
            type: `ARCHIVE_${type}`,
            item: `${current.type}s`,
            token,
            message: t("editor-notif:archiveSuccess"),
            error: t("editor-notif:archiveSuccess"),
            callback: () => {
              if (onArchive) onArchive();
            },
          }),
        );
        break;
      case "delete":
        dispatch(
          delApi({
            endpoint: `${endpoint}${current.uuid}/delete`,
            type: `DELETE_${type}`,
            item: `${current.type}s`,
            headers: [
              {
                key: "xx-ok-project",
                value: projectSlug,
              },
            ],
            token,
            message: t("editor-notif:deleteSuccess"),
            callbackError: () => {
              dispatch(notifError(t("editor-notif:deleteError")));
            },
            callback: () => {
              if (onDelete) onDelete();
              if (url_history) history.push(url_history);
            },
          }),
        );
        break;
      default:
        break;
    }
  };

  function handleOpenChange(open: boolean) {
    setOpened(open);
    onOpenChange?.(open);
  }

  const actionOnType = (nextAction: SettingsActionType) => {
    setOpenModal(true);
    handleOpenChange(false);
    setAction(nextAction);
  };

  const handleActionKeyDown =
    (callback: () => void) => (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        callback();
      }
    };

  const onGetStatus = (resp: StatusResponse) => {
    setCurrent({
      ...current,
      uuid: resp.uuid,
      email: resp.email,
      label: resp.label,
      is_archivable: resp.is_archivable,
      is_deletable: resp.is_deletable,
      is_editable: resp.is_editable && editable,
    });
  };

  const onOpenIcon = (open: boolean) => {
    if (!open) {
      setIsStatusLoading(false);
      handleOpenChange(false);
      return;
    }

    if (open && get_status) {
      setIsStatusLoading(true);
      dispatch(
        getApi({
          endpoint: `${endpoint}${item.uuid}/status`,
          type: `GET_${type}_STATUS`,
          headers: [
            {
              key: "xx-ok-project",
              value: projectSlug,
            },
          ],
          token,
          callback: (resp: StatusResponse) => {
            onGetStatus(resp);
            setIsStatusLoading(false);
          },
          callbackError: () => {
            setIsStatusLoading(false);
          },
        }),
      );
    }
    handleOpenChange(open);
  };

  const modalActions = [
    {
      label: t("modal:cancel"),
      onClick: () => setOpenModal(false),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
    },
    {
      label: t("modal:submit"),
      onClick: onConfirm,
      buttonVariant: BUTTON_VARIANTS.DELETE,
    },
  ];

  const canEdit =
    !show_or_see &&
    !hide_edit &&
    current.uuid === item.uuid &&
    current.is_editable;

  const canImpersonate = current.uuid === item.uuid && current.type === "user";

  const canArchive = current.uuid === item.uuid && current.is_archivable;

  const canDelete = current.uuid === item.uuid && current.is_deletable;

  const dropdownItemClassNames =
    "text-muted-foreground hover:text-muted-foreground! cursor-pointer";

  return (
    <div className={className}>
      <DropdownMenu onOpenChange={(open) => onOpenIcon(open)} open={opened}>
        <DropdownMenuTrigger asChild>
          <Icon
            className="hover:text-primary cursor-pointer"
            path={mdiCog}
            size={1}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isStatusLoading ? (
            <div className="flex w-full items-center justify-center py-3">
              <Icon spin path={mdiLoading} size={0.9} />
            </div>
          ) : (
            <DropdownMenuGroup>
              {show_or_see ? (
                <DropdownMenuItem className={dropdownItemClassNames}>
                  <a className="flex items-center gap-2" href={edit_endpoint}>
                    {current.is_editable ? (
                      <Icon path={mdiPencil} size={0.9} />
                    ) : (
                      <Icon path={mdiEye} size={0.9} />
                    )}
                    {current.is_editable ? t("edit_action") : t("see_more")}
                  </a>
                </DropdownMenuItem>
              ) : null}
              {canEdit ? (
                <DropdownMenuItem className={dropdownItemClassNames}>
                  <a className="flex items-center gap-2" href={edit_endpoint}>
                    <Icon path={mdiPencil} size={0.9} />
                    {t("edit_action")}
                  </a>
                </DropdownMenuItem>
              ) : null}
              {canImpersonate ? (
                <DropdownMenuItem
                  className={dropdownItemClassNames}
                  onClick={() => {
                    impersonateUser.mutate(current.uuid);
                  }}
                  onKeyDown={handleActionKeyDown(() => {
                    impersonateUser.mutate(current.uuid);
                  })}
                >
                  <Icon path={mdiAccountConvert} size={0.9} />
                  {t("impersonate")}
                </DropdownMenuItem>
              ) : null}
              {canArchive ? (
                <DropdownMenuItem
                  className={dropdownItemClassNames}
                  onClick={() => actionOnType("archive")}
                  onKeyDown={handleActionKeyDown(() => actionOnType("archive"))}
                >
                  <Icon path={mdiArchive} size={0.9} />
                  {t("archive")}
                </DropdownMenuItem>
              ) : null}
              {canDelete ? (
                <DropdownMenuItem
                  className={dropdownItemClassNames}
                  onClick={() => actionOnType("delete")}
                  onKeyDown={handleActionKeyDown(() => actionOnType("delete"))}
                >
                  <Icon path={mdiDelete} size={0.9} />
                  {t("delete")}
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmModal
        actions={modalActions}
        iconPath={action === "archive" ? mdiArchive : mdiTrashCanOutline}
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        size="md"
        title={t("modal:confirm")}
      >
        <span className="mb-1.25 block">
          {action === "archive" ? archive_body : delete_body}
        </span>
        <span>{warning}</span>
      </ConfirmModal>
    </div>
  );
}

export default SettingsAction;
